import React, { useEffect, useState } from "react"
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Tabs,
  Tab,
  Accordion,
} from "react-bootstrap"
import { FaArrowLeft, FaPrint, FaDownload } from "react-icons/fa"
import { useLocation, useNavigate } from "react-router-dom"
import CustomBreadcrumb from "../../../components/CustomBreadcrumb"
import { homeIcon } from "../../../constants/icons"
import { generatePracticePaper } from "../../../api/user/practicePaper.api"
import PdfReader from "../../../components/PdfReader"
import { getFileType } from "../../../utils/helper"
import QuestionDetailsModal from "../../../components/modal/QuestionDetailsModal"
import {
  printSingleQuestion,
  printMultipleQuestions,
} from "../../../utils/printQuestions"

const PracticeGeneratorResult = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const { subject, selectedTopics, mode, totalQuestions, perTopicCounts } =
    location.state || {}

  const [generatedQuestions, setGeneratedQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Modal state — reuse the same QuestionDetailsModal from exam papers
  const [showModal, setShowModal] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [printingId, setPrintingId] = useState(null) // tracks which question is printing
  const [printingAll, setPrintingAll] = useState(false)

  useEffect(() => {
    if (!subject) {
      navigate("/practice-generator")
      return
    }

    const fetchPaper = async () => {
      setLoading(true)
      setError(null)
      try {
        const payload = {
          subjectId: subject._id,
          mode,
          totalQuestions: mode === "total" ? totalQuestions : 0,
          selectedTopics: selectedTopics.map((t) => ({
            id: t.id,
            label: t.label,
          })),
          perTopicCounts: mode === "per-topic" ? perTopicCounts : {},
        }
        const response = await generatePracticePaper(payload)

        // The API returns { success, message, data: [...], totalCount }
        const questionsList = response.data ? response.data : response
        if (Array.isArray(questionsList)) {
          setGeneratedQuestions(questionsList)
        } else {
          setGeneratedQuestions(response.questions || [])
        }
      } catch (err) {
        console.error("Error generating paper:", err)
        setError(
          err.response?.data?.message ||
            "An error occurred while generating the practice paper.",
        )
      } finally {
        setLoading(false)
      }
    }

    fetchPaper()
  }, [subject, mode, totalQuestions, selectedTopics, perTopicCounts, navigate])

  if (!subject) return null

  const breadcrumbItems = [
    { label: <>{homeIcon}</>, path: "/" },
    { label: "Practice Generator", path: "/practice-generator" },
    { label: "Preview", path: "/practice-generator/result" },
  ]

  const handleQuestionClick = (question) => {
    setSelectedQuestion(question)
    setShowModal(true)
  }

  const handlePrintQuestion = async (question) => {
    setPrintingId(question._id)
    try {
      await printSingleQuestion(question, {}, false)
    } catch (err) {
      console.error("Print failed:", err)
    } finally {
      setPrintingId(null)
    }
  }

  const handlePrintAll = async () => {
    setPrintingAll(true)
    try {
      await printMultipleQuestions(generatedQuestions, [], false)
    } catch (err) {
      console.error("Print all failed:", err)
    } finally {
      setPrintingAll(false)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedQuestion(null)
  }

  const renderFilePreview = (fileUrl, altText) => {
    if (!fileUrl) {
      return (
        <div
          className='text-center text-muted py-4'
          style={{ backgroundColor: "#f8f9fa", borderRadius: "0.375rem" }}
        >
          No file available
        </div>
      )
    }
    if (getFileType(fileUrl) === "pdf") {
      return (
        <PdfReader
          file={fileUrl}
          noOfPageInPreview={Infinity}
          showFullScreenButton={false}
        />
      )
    }
    return (
      <img
        src={fileUrl}
        alt={altText}
        style={{
          width: "100%",
          borderRadius: "0.375rem",
          border: "1px solid #dee2e6",
        }}
        onError={(e) => {
          e.target.onerror = null
          e.target.src = "https://placehold.co/600x400?text=No+attachment"
        }}
      />
    )
  }

  return (
    <>
      <div className='breadcrum-header d-print-none'>
        <Container>
          <CustomBreadcrumb breadcrumbItems={breadcrumbItems} />
        </Container>
      </div>

      <div className='practice-result-page subject-section'>
        <Container>
          {/* Header */}
          <div className='d-flex justify-content-between align-items-end mb-4 d-print-none'>
            <div>
              <Button
                variant='link'
                className='p-0 text-muted text-decoration-none mb-2 d-flex align-items-center'
                onClick={() => navigate("/practice-generator")}
                style={{ fontWeight: 500 }}
              >
                <FaArrowLeft className='me-2' /> Back to Generator
              </Button>
              <h2 className='mb-0' style={{ margin: 0 }}>
                Paper Preview
              </h2>
            </div>

            <div className='d-flex gap-3'>
              <Button
                variant='outline-primary'
                onClick={handlePrintAll}
                className='d-flex align-items-center gap-2 fw-semibold px-4'
                disabled={loading || !!error || printingAll}
              >
                {printingAll ? (
                  <>
                    <Spinner animation='border' size='sm' /> Preparing...
                  </>
                ) : (
                  <>
                    <FaPrint /> Print All
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className='pg-result-loading'>
              <Spinner
                animation='border'
                variant='primary'
                style={{ width: "3rem", height: "3rem" }}
              />
              <h4>Generating your custom practice paper...</h4>
              <p>
                This might take a few moments depending on the number of
                questions.
              </p>
            </div>
          ) : error ? (
            <div className='pg-result-loading'>
              <Alert variant='danger' className='text-center w-100 mb-4'>
                <h5>Generation Failed</h5>
                <p className='mb-0'>{error}</p>
              </Alert>
              <Button
                variant='primary'
                onClick={() => navigate("/practice-generator")}
              >
                Return to Settings
              </Button>
            </div>
          ) : generatedQuestions.length === 0 ? (
            <div className='pg-result-loading'>
              <h5 className='text-muted'>
                No questions could be generated with the selected criteria.
              </h5>
              <Button
                variant='primary'
                className='mt-3'
                onClick={() => navigate("/practice-generator")}
              >
                Adjust Configuration
              </Button>
            </div>
          ) : (
            <>
              {/* Summary bar */}
              <div className='pg-result-summary d-print-none'>
                <span>
                  <strong>{generatedQuestions.length}</strong> questions
                  generated
                </span>
                <span className='mx-2'>•</span>
                <span>{subject.title}</span>
                <span className='mx-2'>•</span>
                <span>
                  {selectedTopics.length} topic
                  {selectedTopics.length !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Question Cards — Accordion */}
              <Accordion defaultActiveKey='0' className='pg-question-list'>
                {generatedQuestions.map((q, index) => (
                  <Accordion.Item
                    eventKey={String(index)}
                    key={q._id || index}
                    className='pg-question-card'
                  >
                    {/* Accordion Header — always visible */}
                    <Accordion.Header className='pg-accordion-header'>
                      <div className='pg-question-card-header'>
                        <div className='pg-question-title'>
                          <span className='pg-q-num'>Q{index + 1}</span>
                          <span className='pg-q-label'>
                            {q.question_number &&
                              `Question ${q.question_number}`}
                            {q.part && ` ${q.part}`}
                            {q.subpart && ` (${q.subpart})`}
                          </span>
                        </div>
                        <div className='pg-question-badges'>
                          {q.year && (
                            <Badge
                              bg=''
                              style={{
                                backgroundColor: "#7a5d10",
                                color: "white",
                                fontSize: "0.8rem",
                                padding: "0.4em 0.7em",
                              }}
                            >
                              {q.year}
                            </Badge>
                          )}
                          {q.paper && (
                            <Badge
                              bg=''
                              style={{
                                backgroundColor: "#667085",
                                color: "white",
                                fontSize: "0.8rem",
                                padding: "0.4em 0.7em",
                              }}
                            >
                              {q.paper}
                            </Badge>
                          )}
                          {q.marks > 0 && (
                            <Badge
                              bg=''
                              style={{
                                backgroundColor: "#dc2626",
                                color: "white",
                                fontSize: "0.8rem",
                                padding: "0.4em 0.7em",
                              }}
                            >
                              {q.marks} mark{q.marks !== 1 ? "s" : ""}
                            </Badge>
                          )}
                          {q.topics?.length > 0 &&
                            q.topics.map((t) => (
                              <Badge
                                key={t._id}
                                pill
                                bg='light'
                                text='dark'
                                style={{
                                  fontSize: "0.8rem",
                                  padding: "0.4em 0.7em",
                                }}
                              >
                                {t.title}
                              </Badge>
                            ))}
                        </div>
                      </div>
                    </Accordion.Header>

                    {/* Accordion Body — collapsible */}
                    <Accordion.Body className='pg-accordion-body'>
                      {/* Subtopics row */}
                      {q.subtopics?.length > 0 && (
                        <div className='pg-question-meta'>
                          <div className='pg-meta-group'>
                            <span className='pg-meta-label'>Subtopics:</span>
                            {q.subtopics.map((st) => (
                              <Badge
                                key={st._id}
                                pill
                                bg='light'
                                text='dark'
                                style={{
                                  fontSize: "0.8rem",
                                  padding: "0.4em 0.7em",
                                }}
                              >
                                {st.title}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tabbed Content: Question / Marking Scheme / Solution */}
                      <div className='pg-question-tabs-wrap'>
                        <Tabs
                          defaultActiveKey='question'
                          id={`pg-tabs-${q._id || index}`}
                          className='mb-3 question-tabs'
                        >
                          <Tab eventKey='question' title='Question'>
                            <div className='pg-tab-content'>
                              {renderFilePreview(
                                q.question_file,
                                `Question ${index + 1}`,
                              )}
                            </div>
                          </Tab>
                          <Tab eventKey='marking-scheme' title='Marking Scheme'>
                            <div className='pg-tab-content'>
                              {renderFilePreview(
                                q.ms_file,
                                `Marking Scheme ${index + 1}`,
                              )}
                            </div>
                          </Tab>
                          <Tab eventKey='solution' title='Solution'>
                            <div className='pg-tab-content'>
                              {renderFilePreview(
                                q.solution_file,
                                `Solution ${index + 1}`,
                              )}
                            </div>
                          </Tab>
                          {q.dependencies?.length > 0 && (
                            <Tab eventKey='dependencies' title='Dependencies'>
                              <div className='pg-tab-content'>
                                {q.dependencies.map((dep, depIdx) => (
                                  <div
                                    key={dep._id || depIdx}
                                    style={{ marginBottom: "1rem" }}
                                  >
                                    {renderFilePreview(
                                      dep.file_name,
                                      `Dependency ${depIdx + 1}`,
                                    )}
                                  </div>
                                ))}
                              </div>
                            </Tab>
                          )}
                        </Tabs>
                      </div>

                      {/* Footer */}
                      <div className='pg-question-card-footer'>
                        <Button
                          variant='link'
                          size='sm'
                          className='text-decoration-none'
                          onClick={() => handleQuestionClick(q)}
                          style={{ color: "#7a5d10", fontWeight: 600 }}
                        >
                          Open in Full View →
                        </Button>
                        <Button
                          variant='outline-primary'
                          size='sm'
                          className='d-flex align-items-center gap-2 fw-semibold'
                          onClick={() => handlePrintQuestion(q)}
                          disabled={printingId === q._id}
                        >
                          {printingId === q._id ? (
                            <>
                              <Spinner animation='border' size='sm' />{" "}
                              Preparing...
                            </>
                          ) : (
                            <>
                              <FaPrint /> Print to PDF
                            </>
                          )}
                        </Button>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </>
          )}
        </Container>
      </div>

      {/* Reuse the existing QuestionDetailsModal for full-view */}
      <QuestionDetailsModal
        show={showModal}
        handleClose={handleCloseModal}
        question={selectedQuestion}
        loading={false}
      />
    </>
  )
}

export default PracticeGeneratorResult
